import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { DevelopmentService } from "@services/development.service";

import esriConfig from "@arcgis/core/config";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";

@Component({
  selector: "app-arcgis-map",
  templateUrl: "./arcgis-map.component.html",
  styleUrls: ["./arcgis-map.component.scss"],
})
export class ArcgisMapComponent implements OnInit {
  @Input() zoom = 10;
  @Input() center: Array<number> = [-73.95, 40.726];
  @Input() showLocationCircle = true;
  @Input() isProjectPage = false;
  @Input() basemapType = "streets-navigation-vector";
  @Input()
  set inputDevelopmentId(id: number) {
    this._inputDevelopmentId = id;

    if (id && this.isMapLoaded && id != this.selectedDevelopmentId) {
      this.goToDevelopment(id);
    }
  }

  get inputDevelopmentId(): number {
    return this._inputDevelopmentId;
  }

  @Input()
  set moveTo(center: Array<number>) {
    if (center?.length && this.isMapLoaded) {
      this.mapView.goTo({
        center: center,
        zoom: JSON.stringify(center) == "[-73.95,40.726]" ? 10 : 11,
      });
    }
  }

  @Output() mapLoaded = new EventEmitter<boolean>();
  @Output() selectedDevelopment = new EventEmitter<number>();

  @ViewChild("mapViewNode", { static: true }) private mapViewEl!: ElementRef;

  basemapTypes = ["streets", "streets-navigation-vector", "topo", "arcgis-topographic"];
  color = "#fff";
  background = "rgba(30, 30, 30, 0.6)";
  fontSize = "9";
  fontWeight = "400";
  isLandingPage = true;
  developmentList: any[] = [];
  developmentNameField = ["Developmen", "DEVELOPMEN", "Development"];
  developmentNameProperty = "";
  developmentIdProperty = "TDS";

  hoveredDevelopmentName = "development Name";
  selectedDevelopmentId = 0;
  lastSelectedDevelopmentId = 0;

  mapView!: MapView;
  developmentsShapeLayer!: FeatureLayer;
  developmentCircleLayer!: FeatureLayer;
  buildingLayoutLayer!: FeatureLayer;
  extent!: __esri.Extent;
  isMapLoaded = false;
  _inputDevelopmentId!: number;

  constructor(private developmentService: DevelopmentService) { }

  async initializeMap() {
    try {
      // Point @arcgis/core to the locally-copied worker assets (no CDN, no inline scripts)
      esriConfig.assetsPath = "/assets/esri";

      // Create feature layers from feature services
      this.developmentsShapeLayer = new FeatureLayer({
        url: "https://services1.arcgis.com/0mhlGDIUKwQF6tnJ/ArcGIS/rest/services/NYCHA_Developments_Tracker/FeatureServer/1",
        outFields: ["Developmen", "*"],
      });

      this.buildingLayoutLayer = new FeatureLayer({
        url: "https://services1.arcgis.com/0mhlGDIUKwQF6tnJ/ArcGIS/rest/services/NYCHA_Building_PublicNoFilter/FeatureServer/0",
        outFields: ["TDS_NUM"],
      });

      this.developmentCircleLayer = new FeatureLayer({
        url: "https://services1.arcgis.com/0mhlGDIUKwQF6tnJ/ArcGIS/rest/services/NYCHA_Developments_Tracker/FeatureServer/0",
        outFields: ["Developmen", "*"],
      });

      this.developmentsShapeLayer.renderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({
          color: "rgba(140, 41, 7, 0.25)",
          outline: new SimpleLineSymbol({
            width: 1,
            color: "rgba(140, 41, 7, 1)",
          }),
        }),
      });

      this.buildingLayoutLayer.renderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({
          color: "rgba(140, 41, 7, 1)",
          outline: new SimpleLineSymbol({
            width: 1,
            color: "rgba(140, 41, 7, 1)",
          }),
        }),
      });

      if (this.isProjectPage) {
        this.buildingLayoutLayer.labelingInfo = [
          new LabelClass({
            labelExpressionInfo: { expression: "$feature.BLDG_LABEL" },
            symbol: {
              type: "text",
              color: "black",
              font: {
                size: 12,
              },
              haloSize: 1,
              haloColor: "white",
            } as any,
          }),
        ];
      }

      if (this.showLocationCircle) {
        this.developmentCircleLayer.renderer = new SimpleRenderer({
          symbol: new SimpleMarkerSymbol({
            color: "rgba(99, 166, 158, 1)",
            size: 10,
            outline: new SimpleLineSymbol({
              width: 8,
              color: "rgba(99, 166, 158, 0.47)",
            }),
          }),
        });
      } else {
        this.developmentCircleLayer.renderer = new SimpleRenderer({
          symbol: new SimpleMarkerSymbol({
            color: "rgba(0, 0, 0, 0)",
            size: 0,
          }),
        });
      }

      const map = new Map({
        basemap: this.basemapType as __esri.BasemapProperties,
        layers: [
          this.developmentsShapeLayer,
          this.buildingLayoutLayer,
          this.developmentCircleLayer,
        ],
      });

      this.mapView = new MapView({
        container: this.mapViewEl.nativeElement,
        center: this.center as any,
        zoom: this.zoom,
        map: map,
      });

      this.developmentNameProperty = this.isLandingPage
        ? this.developmentNameField[0]
        : this.developmentNameField[1];

      this.mapView.on("pointer-move", (evt: any) => {
        this.mapView.hitTest(evt, { exclude: this.mapView.graphics }).then((r: any) => {
          const graphicHit = r.results.find(
            (result: any): result is __esri.GraphicHit =>
              result.type === "graphic" &&
              !!(result as __esri.GraphicHit).graphic?.attributes[this.developmentNameProperty],
          ) as __esri.GraphicHit | undefined;

          const hoverBox = document.getElementById("hoverBox");
          if (graphicHit && hoverBox) {
            hoverBox.style.display = "block";
            hoverBox.style.top = evt.y - 28 + "px";
            hoverBox.style.left = evt.x + "px";
            this.hoveredDevelopmentName = graphicHit.graphic?.attributes[this.developmentNameProperty];
          } else if (hoverBox) {
            hoverBox.style.display = "none";
          }
        });
      });

      this.mapView.on("click", (evt: any) => {
        this.mapView.hitTest(evt).then((r: any) => {
          // Search through all hit results to find one with a valid development ID
          const matchedHit = r.results.find(
            (result: any): result is __esri.GraphicHit =>
              result.type === "graphic" &&
              !!(result as __esri.GraphicHit).graphic?.attributes[this.developmentIdProperty],
          ) as __esri.GraphicHit | undefined;

          if (matchedHit) {
            const developmentId = matchedHit.graphic.attributes[this.developmentIdProperty];

            if (
              this.lastSelectedDevelopmentId === 0 ||
              developmentId !== this.lastSelectedDevelopmentId
            ) {
              this.selectedDevelopmentId = developmentId;
              this.lastSelectedDevelopmentId = this.selectedDevelopmentId;
              this.selectedDevelopment.emit(this.selectedDevelopmentId);
            }

            this.goToDevelopment(developmentId);
          }
          // If no valid development point was clicked, do nothing
        });
      });

      // All resources in the MapView and the map have loaded.
      // Now execute additional processes
      this.mapView.when(async () => {
        this.extent = this.mapView.extent;

        this.getAllDevelopments().then(() => {
          // Apply the circle layer filter AFTER developmentList is populated
          // so objectIds correctly excludes "Excluded" developments
          this.displayDevelopment(this.developmentCircleLayer, "filtered");
          this.isMapLoaded = true;
          this.goToDevelopment(this._inputDevelopmentId);
          this.mapLoaded.emit(true);
        });
      });

      this.displayDevelopment(this.developmentsShapeLayer, "null");
      this.displayDevelopment(this.buildingLayoutLayer, "null");
      // Note: developmentCircleLayer filter is applied inside getAllDevelopments().then()
    } catch (error) {
      alert("We have an error: " + error);
    }
  }

  ngOnInit() {
    this.initializeMap();
  }

  goToDevelopment(id: number): void {
    if (id) {
      const development = this.developmentList.find((item) => item.id == id);

      if (development) {
        this.displayDevelopment(this.developmentsShapeLayer, `TDS = ${id}`);
        this.displayDevelopment(
          this.buildingLayoutLayer,
          `TDS_NUM = '${id < 100 ? "0" + id : id}'`,
        );

        this.mapView.goTo({
          center: [development.longitude, development.latitude],
          zoom: 14,
        });
      }
    }
  }

  async getAllDevelopments(): Promise<any[]> {
    const parcelQuery = {
      where: `NOT ${this.developmentIdProperty} = ''`,
      geometry: this.extent,
      outFields: ["*"],
      returnGeometry: true,
    };

    await this.developmentCircleLayer
      ?.queryFeatures(parcelQuery)
      .then((results: any) => {
        for (const development of results.features) {
          this.developmentList.push({
            id: development.attributes[this.developmentIdProperty],
            name: development.attributes[this.developmentNameProperty],
            objectId: development.attributes["OBJECTID"],
            latitude: (development as any).geometry["latitude"],
            longitude: (development as any).geometry["longitude"],
          });
        }
      })
      .catch((error: any) => {
        console.error("error", error);
      });

    return this.developmentList.sort((a, b) => a.id - b.id);
  }

  displayDevelopment(layer: FeatureLayer, whereClause: string): void {
    this.mapView.whenLayerView(layer).then((layerView: any) => {
      if (whereClause == "null" || whereClause.includes("=")) {
        layerView.filter = new FeatureFilter({
          where: whereClause,
        });
      } else {
        layerView.filter = new FeatureFilter({
          objectIds: this.developmentList
            .filter((l) => this.developmentService.getAllowedDevelopments().includes(l.id))
            .map((d) => d.objectId),
        });
      }
    });
  }
}
